import { Trans, useTranslation } from 'react-i18next';

import { Button, Modal } from 'components/ui';
import { DamageLink, ObservationLink } from 'horizon/components/Damages/utils';
import { type AtlasGqlHorizonDamageObservationGroup } from 'types/atlas-graphql';

type RemoveConfirmationModalProps = {
  damageId: string;
  observationId: string;
  groupForObservation?: AtlasGqlHorizonDamageObservationGroup;
  isOpen: boolean;
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

export const RemoveConfirmationModal: React.FunctionComponent<RemoveConfirmationModalProps> = ({
  damageId,
  observationId,
  groupForObservation,
  isOpen,
  loading,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();
  const observation = groupForObservation?.observations?.find(o => o?.id === observationId);
  const remainingObservationsInGroup =
    groupForObservation?.observations?.filter(o => o?.id !== observationId) ?? [];

  const damageLink = <DamageLink damageId={damageId} target={'_blank'} />;
  const observationLink = (
    <ObservationLink
      observationId={observationId}
      pictureId={observation?.picture?.id}
      target={'_blank'}
    />
  );

  return (
    <Modal
      title={t('damage.unlinkObservation', 'Unlink Observation')}
      visible={isOpen}
      onCancel={onClose}
      destroyOnClose={true}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={onConfirm} loading={loading} type="primary">
            {t('damage.unlink', 'Unlink')}
          </Button>
        </>
      }
    >
      {groupForObservation?.isUserEdited ? (
        remainingObservationsInGroup.length === 1 ? (
          <Trans
            i18nKey="damage.unlinkObservationUserEditedSingle"
            t={t}
            components={{ observationLink, damageLink }}
            defaults="Observation <observationLink/> will be unlinked from Damage <damageLink/>. Any Observation Group attributes that were manually altered will not be preserved."
          />
        ) : (
          <Trans
            i18nKey="damage.unlinkObservationUserEditedMultiple"
            t={t}
            components={{ observationLink, damageLink }}
            defaults="Observation <observationLink/> will be unlinked from Damage <damageLink/>. Observation Group attributes will not be applied to Observation <observationLink/>."
          />
        )
      ) : (
        <Trans
          i18nKey="damage.unlinkObservationSimple"
          t={t}
          components={{ observationLink, damageLink }}
          defaults="Observation <observationLink/> will be unlinked from Damage <damageLink/>."
        />
      )}
    </Modal>
  );
};
